/**
 * <ChatMarkdown /> — renders LLM-generated markdown in the chat
 * panel.
 *
 * The model replies in markdown — **bold**, lists, headings, code,
 * tables, links, blockquotes. Rendering it raw showed the literal
 * `**` etc. This wraps `react-markdown` + `remark-gfm` (GFM:
 * tables, strikethrough, task lists, autolinks) and maps every
 * element to a design-system-styled node.
 *
 * Raw HTML is intentionally NOT enabled (no `rehype-raw`) — the
 * model's output is escaped, so a stray `<script>` can never run.
 *
 * Streaming-safe: an unclosed `**bold` just renders as literal text
 * until the closing token arrives, then snaps to bold.
 */

import type { ComponentPropsWithoutRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

type ElProps<T extends keyof JSX.IntrinsicElements> =
  ComponentPropsWithoutRef<T>;

const COMPONENTS = {
  p: (props: ElProps<"p">) => (
    <p className="text-sm leading-relaxed text-text-primary" {...props} />
  ),
  strong: (props: ElProps<"strong">) => (
    <strong className="font-semibold text-text-primary" {...props} />
  ),
  em: (props: ElProps<"em">) => <em className="italic" {...props} />,
  del: (props: ElProps<"del">) => (
    <del className="text-text-tertiary line-through" {...props} />
  ),
  a: ({ href, ...props }: ElProps<"a">) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-brand-blue underline underline-offset-2 hover:no-underline"
      {...props}
    />
  ),
  ul: (props: ElProps<"ul">) => (
    <ul
      className="list-disc space-y-0.5 ps-5 text-sm text-text-primary"
      {...props}
    />
  ),
  ol: (props: ElProps<"ol">) => (
    <ol
      className="list-decimal space-y-0.5 ps-5 text-sm text-text-primary"
      {...props}
    />
  ),
  li: (props: ElProps<"li">) => (
    <li className="leading-relaxed" {...props} />
  ),
  h1: (props: ElProps<"h1">) => (
    <h1
      className="mt-1 text-base font-semibold text-text-primary"
      {...props}
    />
  ),
  h2: (props: ElProps<"h2">) => (
    <h2
      className="mt-1 text-sm font-semibold text-text-primary"
      {...props}
    />
  ),
  h3: (props: ElProps<"h3">) => (
    <h3
      className="mt-1 text-sm font-semibold text-text-secondary"
      {...props}
    />
  ),
  blockquote: (props: ElProps<"blockquote">) => (
    <blockquote
      className="border-s-2 border-border-default ps-3 text-sm italic text-text-secondary"
      {...props}
    />
  ),
  hr: (props: ElProps<"hr">) => (
    <hr className="border-border-subtle" {...props} />
  ),
  code: (props: ElProps<"code">) => (
    // Inline code; fenced blocks are wrapped in <pre> (styled
    // below) which strips this background via the [&>code] reset.
    <code
      className="rounded bg-surface-sunken px-1 py-0.5 font-mono text-[0.85em] text-text-primary"
      {...props}
    />
  ),
  pre: (props: ElProps<"pre">) => (
    <pre
      className="overflow-x-auto rounded-md bg-surface-sunken p-2.5 text-xs leading-relaxed [&>code]:bg-transparent [&>code]:p-0"
      {...props}
    />
  ),
  table: (props: ElProps<"table">) => (
    <div className="overflow-x-auto">
      <table
        className="w-full border-collapse text-xs"
        {...props}
      />
    </div>
  ),
  th: (props: ElProps<"th">) => (
    <th
      className="border border-border-subtle bg-surface-sunken px-2 py-1 text-start font-semibold"
      {...props}
    />
  ),
  td: (props: ElProps<"td">) => (
    <td
      className="border border-border-subtle px-2 py-1 text-start"
      {...props}
    />
  ),
};

export function ChatMarkdown({ children }: { children: string }) {
  return (
    <div className="flex flex-col gap-2 break-words text-sm leading-relaxed text-text-primary">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
